import { useLife } from '@/state/life';
import { usePower } from '@/state/power';
import { useXClone } from '@/state/xclone';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useCinematic } from '@/components/CinematicTakeover';
import { useMeta } from '@/state/meta';
import { sounds } from '@/audio/synth';
import { PHONE_ACTION_HANDLERS } from './phoneActions';

/**
 * Bloomberg Interview arc.
 *
 * Triggered as phone offer when notoriety >= 40 + cash >= $1M. 4
 * journalist questions, branching tribal effects. Different from press
 * conference (you control), this is hostile (they control).
 */

interface Beat {
  text: string;
  options: { label: string; effect: () => void }[];
}

const BEATS: Beat[] = [
  {
    text: 'BLOOMBERG REPORTER: "you\'ve been described as a \"degenerate trader.\" do you reject that label?"',
    options: [
      { label: '"i embrace it. i\'ve trademarked it. ask my lawyer."', effect: () => {
        usePower.getState().bumpRep('wsb', 18);
        usePower.getState().bumpNotoriety(8);
      } },
      { label: '"i reject it. i\'m a value investor."', effect: () => {
        usePower.getState().bumpRep('cnbc', 6);
        usePower.getState().bumpRep('wsb', -8);
      } },
      { label: '"the label predates me by 14 years. i\'m simply the latest carrier."', effect: () => {
        usePower.getState().bumpRep('press', 8);
      } },
    ],
  },
  {
    text: 'REPORTER: "your portfolio drawdown was 47% in March. how do you sleep at night?"',
    options: [
      { label: '"on a mattress i bought from $ZZZZ at 26% APR."', effect: () => {
        usePower.getState().bumpRep('press', 6);
        usePower.getState().bumpNotoriety(4);
      } },
      { label: '"i don\'t sleep. that\'s the alpha."', effect: () => {
        usePower.getState().bumpRep('wsb', 12);
        useLife.getState().bumpSanity(-3);
      } },
      { label: '"my drawdowns are private."', effect: () => {
        usePower.getState().bumpRep('press', -4);
      } },
    ],
  },
  {
    text: 'REPORTER: "your relationship with [foreign entity] has come up. care to clarify?"',
    options: [
      { label: '"i don\'t know what entity you\'re referring to."', effect: () => {
        useLife.getState().bumpFbiHeat(8);
        usePower.getState().bumpRep('press', -8);
      } },
      { label: '"i refer all foreign-policy questions to my lawyer."', effect: () => {
        usePower.getState().bumpRep('press', 4);
      } },
      { label: '"yes. they\'re great people."', effect: () => {
        useLife.getState().bumpFbiHeat(15);
        usePower.getState().bumpNotoriety(15);
      } },
    ],
  },
  {
    text: 'REPORTER: "if you could give one piece of advice to a 22-year-old retail trader, what would it be?"',
    options: [
      { label: '"don\'t."', effect: () => {
        usePower.getState().bumpRep('press', 12);
        usePower.getState().bumpRep('wsb', 14);
      } },
      { label: '"buy index funds."', effect: () => {
        usePower.getState().bumpRep('cnbc', 6);
      } },
      { label: '"call your mother."', effect: () => {
        useLife.getState().bumpRelationship('mom', 8);
        usePower.getState().bumpRep('press', 4);
      } },
    ],
  },
];

export function startBloombergInterviewArc(): void {
  useMeta.getState().recordLore('arc-bloomberg-interview', 'Bloomberg Interview');
  useCinematic.getState().fire({
    id: `bloomberg-${Date.now()}`,
    type: 'breaking',
    headline: 'BLOOMBERG INTERVIEW — LIVE.',
    subhead: '4 questions. she has a research file. you have caffeine. tape will run regardless. pre-roll is over.',
    durationMs: 5500,
  });
  sounds.notifyAlert();

  for (let i = 0; i < BEATS.length; i++) {
    const b = BEATS[i]!;
    setTimeout(() => {
      const ids = ['a', 'b', 'c'];
      const actions = b.options.map((opt, idx) => ({
        id: ids[idx]!,
        label: opt.label,
        effectId: `bloom-q${i}-${ids[idx]}`,
      }));
      for (let j = 0; j < b.options.length; j++) {
        const id = `bloom-q${i}-${ids[j]}`;
        const opt = b.options[j]!;
        PHONE_ACTION_HANDLERS[id] = () => {
          opt.effect();
          sounds.fill();
        };
      }
      usePhone.getState().receiveMessage({
        threadId: 'broker',
        fromNpc: 'broker',
        text: b.text,
        actions,
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: `bloomberg: Q ${i + 1}/4` });
      sounds.notifyAlert();
    }, 5_000 + i * 28_000);
  }

  setTimeout(() => {
    useXClone.getState().receivePost({
      authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
      text: 'BLOOMBERG: a "well-known retail trader" sat for 14 minutes and gave 4 answers, three of which will be transcribed and one of which will be sealed at his lawyer\'s request.',
      likes: 18000, reposts: 5400,
    });
  }, 5_000 + BEATS.length * 28_000 + 5_000);
}
